import * as moment from 'moment';
import { SearchFilterGroupData, AndOrOperator, SearchFilterData, FilterOperator, PagingModel, BaseModel, SortModel } from '@satin/core/model';

/**
 * This returns the value of the property from an object.
 * Supports complex objects. (ex - user.contact.name)
 *
 * @param object The object
 * @param attributeField The attribute that contains the value to find.
 */
function getObjectValue(object, attributeField) {
    if (isNull(object)) {
        console.error("The option given is null!");
        return;
    }
    if (isNull(attributeField)) {
        console.error("No text field property provided.");
        return;
    }
    if (isNestedProperty(attributeField)) {
        const topLevelPropertyPath = getTopLevelPropertyPath(attributeField);
        const nestedPropertyPath = getNestedPropertyPath(attributeField);
        return getObjectValue(object[topLevelPropertyPath], nestedPropertyPath);
    }
    else {
        return object[attributeField];
    }
}
/**
 * Filters the option list provided using the property and filter value.
 *
 * @param filterValue The user's input or search string
 * @param optionList The list of options in the dropdown
 * @param optionProperty The property of the option object to search
 * @return the list of filtered options
 */
function filterOptionList(filterValue, optionList, optionProperty) {
    const filterText = filterValue.toLowerCase();
    const valueList = forceCopyArray(optionList);
    return valueList.filter((option => {
        return doesOptionContainText(option, filterText, optionProperty);
    }));
}
/**
 * Returns if the option provided contain the string in the appropriate field.
 *
 * @param option The option object
 * @param filterText The filter text to search
 * @param textFieldProperty The property of the option object to search
 * @return boolean
 */
function doesOptionContainText(option, filterText, textFieldProperty) {
    if (isNestedProperty(textFieldProperty)) {
        const topLevelPropertyPath = getTopLevelPropertyPath(textFieldProperty);
        const nestedPropertyPath = getNestedPropertyPath(textFieldProperty);
        return doesOptionContainText(option[topLevelPropertyPath], filterText, nestedPropertyPath);
    }
    return option[textFieldProperty].toLowerCase().includes(filterText);
}
/**
 * Returns the value of a nested property path.
 * ie. user.contact.name => contact.name
 *
 * @param textFieldProperty The text field property name.
 * @return string nested string value
 */
function getNestedPropertyPath(textFieldProperty) {
    if (isNull(textFieldProperty)) {
        console.error("The text field property provided is null.");
    }
    const nestedPropertyArray = getNestedPropertyArray(textFieldProperty);
    const nestedPropertyCount = nestedPropertyArray.length;
    return nestedPropertyArray.slice(1, nestedPropertyCount + 1).join(".");
}
/**
 * Returns the top level attribute of a complex property string.
 * ie. user.contact.name => user
 *
 * @param textFieldProperty The text field property name
 * @return string top level string value
 */
function getTopLevelPropertyPath(textFieldProperty) {
    if (isNotNestedProperty(textFieldProperty)) {
        console.error("The property given is not a nested property path.");
    }
    const nestedPropertyArray = getNestedPropertyArray(textFieldProperty);
    return nestedPropertyArray[0];
}
/**
 * Return a string array from a complex object string property.
 * ie. user.contact.name => [user, contact, name]
 *
 * @param textFieldProperty
 * @return string array
 */
function getNestedPropertyArray(textFieldProperty) {
    if (isNotNestedProperty(textFieldProperty)) {
        console.error("The property given is not a nested property path.");
    }
    return textFieldProperty.split(".");
}
/**
 * Determines if the field property is not a nested property.
 *
 * @param value The object property string
 * @returns True if not nested, false otherwise.
 */
function isNotNestedProperty(value) {
    return !isNestedProperty(value);
}
/**
 * Determines if the field property is a nested property.
 *
 * @param value The object property string
 * @returns True if nested, false otherwise.
 */
function isNestedProperty(value) {
    if (isNull(value)) {
        return false;
    }
    return value.includes(".");
}

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
/**
 * Checks if a property of an object is not null or not undefined.
 *
 * @param baseObject The base object to which the property belongs.
 * @param propertyName The property of that baseObject to be tested.
 * @returns True if not null, false otherwise.
 */
function isNotNullNested(baseObject, propertyName) {
    return isNotNull(getNestedValue(baseObject, propertyName));
}
/**
 * Checks if a property of an object is null or undefined.
 *
 * @param baseObject The base object to which the property belongs.
 * @param propertyName The property of that baseObject to be tested.
 * @returns True if not null, false otherwise.
 */
function isNullNested(baseObject, propertyName) {
    return isNull(getNestedValue(baseObject, propertyName));
}
/**
 * Digs into an object to get the nested value based on a string with potentially '.'s in it
 * @param baseObject The base object to which the property belongs
 * @param propertyName property which value we're looking for
 * @returns the nested value - will return null if no nested value found
 */
function getNestedValue(baseObject, propertyName) {
    return propertyName.split(".").reduce(function (o, x) {
        return (isNotNull(o)) ? o[x] : o;
    }, baseObject);
}
/**
 * Checks if a value is not null, not undefined or not empty.
 *
 * @param value The value to be to be tested.
 * @returns True if not null, false otherwise.
 */
function isNotNull(value) {
    let isNotNull = true;
    try {
        if (value == null || value === undefined || value === null || typeof value === "undefined" || value === "undefined" || value === "" || value === "[object Object]") {
            isNotNull = false;
        }
    }
    catch (err) {
        isNotNull = false;
    }
    return isNotNull;
}
/**
 * Checks if a value is null.
 *
 * @param value The value to be tested.
 * @returns True if null, false otherwise.
 */
function isNull(value) {
    return !isNotNull(value);
}
/**
 * Checks if a value, array or object is empty.
 *
 * @param obj The value, array or object to be tested.
 * @returns True if empty, false otherwise.
 */
function isEmpty(obj) {
    if (isNull(obj)) {
        return true;
    }
    if (obj.constructor === Array) {
        return isEmptyArray(obj);
    }
    else {
        return isEmptyObject(obj);
    }
}
/**
 * Checks if a value, array or object is not empty.
 *
 * @param obj The value, array or object to be tested.
 * @returns True if not empty, false otherwise.
 */
function isNotEmpty(obj) {
    return !isEmpty(obj);
}
/**
 * Checks if an object is empty.
 *
 * @param obj The object to be tested.
 * @returns True if empty, false otherwise.
 */
function isEmptyObject(obj) {
    return (Object.keys(obj).length === 0);
}
/**
 * Checks if an object is not empty.
 *
 * @param obj The object to be tested.
 * @returns True if not empty, false otherwise.
 */
function isNotEmptyObject(obj) {
    return !isEmptyObject(obj);
}
/**
 * Checks if an array is empty.
 *
 * @param value The array to be tested.
 * @returns True if empty, false otherwise.
 */
function isEmptyArray(value) {
    if (isNull(value)) {
        return true;
    }
    return !(value.length > 0);
}
/**
 * Checks if an array is not empty.
 *
 * @param value The array to be tested.
 * @returns True if not empty, false otherwise.
 */
function isNotEmptyArray(value) {
    return !isEmpty(value);
}
/**
 * This method left pads a string with any given character
 *
 * @param text
 * @param size
 * @param padChar
 * @returns True or false
 */
function leftPad(text, size, padChar) {
    return (String(padChar || '0').repeat(size) + text).substr((size * -1), size);
}
/**
 * Checks if the value is a date.
 *
 * @param value Value to be tested.
 * @returns True if a date, false otherwise.
 */
function isDate(value) {
    /*
    So Javascript is odd and identifies numbers as dates if they are 6 digits or less, but it just applies them to years.
    I'm going to just do a quick override check until another solution is found.
    Might do a regex check
     */
    if (isNull(value) || isNull(value.toString()) || isNull(value.toString().length) || value.toString().length <= 6) {
        return false;
    }
    const tempDate = convertDate(value);
    //Check if the value is a date
    return !isNaN(tempDate.getTime());
}
/* Added these functions for saved search filters*/
function isFilterDate(value) {
    if (isNull(value) || isNull(value.toString()) || isNull(value.toString().length) || value.toString().length <= 6) {
        return false;
    }
    const tempDate = convertFilterDate(value);
    return !isNaN(tempDate.getDate());
}
// Output Format: yyyy-mm-dd;
function convertFilterDate(value) {
    try {
        if (isNull(value) || isNull(value.toString()) || isNull(value.toString().length) || value.toString().length <= 6) {
            return null;
        }
        let year, month, day;
        if (value instanceof Date) {
            year = value.getFullYear();
            month = value.getMonth();
            day = value.getDate();
        }
        else {
            const dateArray = value.toString().split(/-| |:|T|\\./);
            year = dateArray[0];
            month = dateArray[1] - 1;
            day = dateArray[2];
        }
        return new Date(year, month, day);
    }
    catch (err) {
        return null;
    }
}
/**
 * Removes spaces from a string.
 *
 * @param value The string to have spaces removed.
 * @returns The new string with the spaces removed.
 */
function removeSpacesFromString(value) {
    return value.replace(/\s+/g, '');
}
/**
 * Removes space character from a string.
 *
 * @param value The string to have space characters removed.
 * @returns The new string with the space characters removed.
 */
function removeSpaceCharactersFromString(value) {
    return value.replace(/[^a-zA-Z0-9]/g, '');
}
/**
 * Converts a value to a date.
 *
 * @param value Value to be converted to a date.
 * @return Date, or null if there is an error.
 */
function convertDate(value) {
    try {
        if (isNull(value) || isNull(value.toString()) || isNull(value.toString().length) || value.toString().length <= 6) {
            return null;
        }
        let year, month, day, hour, minute, second;
        if (value instanceof Date) {
            year = value.getFullYear();
            month = value.getMonth();
            day = value.getDate();
            hour = value.getHours();
            minute = value.getMinutes();
            second = value.getSeconds();
        }
        else {
            const dateArray = value.toString().split(/-| |:|T|\\./);
            year = dateArray[0];
            month = dateArray[1] - 1;
            day = dateArray[2];
            hour = parseInt(dateArray[3]);
            minute = dateArray[4];
            second = dateArray[5];
        }
        return new Date(year, month, day, hour, minute, second);
    }
    catch (err) {
        return null;
    }
}
/**
 * Converts a value to a time.
 *
 * @param value Value to be converted to a time.
 * @returns The value converted to a time.
 */
function convertToTime(value) {
    let dateTime = null;
    if (isNotNull(value)) {
        const date = convertDate(value);
        dateTime = date.getTime();
    }
    return dateTime;
}
/**
 * Gets today's date.
 *
 * @returns Today's date.
 */
function getTodayDate() {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
}
/**
 * Checks if a string contains a character.
 *
 * @param text The text string to be searched for the character param.
 * @param character The character to be searched for in the text param.
 * @returns True if the character is found in the text, false otherwise.
 */
function contains(text, character) {
    if (isNull(text) || isNull(character)) {
        return false;
    }
    return text.indexOf(character) > -1;
}
/**
 * Checks if an element contains elements.
 *
 * @param elementsToIgnore Elements to be ignored.
 * @param elementsToEnforce Elements to be enforced.
 * @param target The target element.
 * @returns True if found, false otherwise.
 */
function elementContainsElements(elementsToIgnore, elementsToEnforce, target) {
    if (isNotNull(elementsToEnforce) && isNotEmpty(elementsToEnforce)) {
        for (const x in elementsToEnforce) {
            if (elementsToEnforce[x].nativeElement.contains(target)) {
                return false;
            }
        }
    }
    if (isNotNull(elementsToIgnore) && isNotEmpty(elementsToIgnore)) {
        for (const x in elementsToIgnore) {
            if (elementsToIgnore[x].nativeElement.contains(target)) {
                return true;
            }
        }
    }
    return false;
}
function forceCopy(value) {
    return Object.assign({}, value);
}
/*
This function is used to force a copy of an object so you don't run into shared memory allocation
 */
function forceCopyArray(value) {
    return Object.assign([], value);
}
/**
 * Method to subtract a number of days.
 *
 * @param date The date to subtract from
 * @param days Days to Subtract
 * @return Evaluated date or undefined
 */
function subtractDays(date, days) {
    if (isNull(days)) {
        return null;
    }
    if (isNull(date)) {
        return null;
    }
    return moment(date, moment.ISO_8601).subtract(days, 'days').toDate();
}
/**
 * Method to add a number days.
 *
 * @param date The date to add days to
 * @param days Days to add
 * @return Evaluated date or undefined
 */
function addDays(date, days) {
    if (isNull(days)) {
        return null;
    }
    if (isNull(date)) {
        return null;
    }
    return moment(date, moment.ISO_8601).add(days, 'days').toDate();
}
/**
 * Checks if item is an object.
 *
 * @param item - Element to test
 * @return true if item is an object, false if not
 * */
function isObject(item) {
    if (isNull(item)) {
        return false;
    }
    return (typeof item === 'object') || (item.constructor === Object);
}
/**
 * Checks if item is not an object.
 *
 * @param item - Element to test
 * @return true if item is not a object, false if not
 * */
function isNotObject(item) {
    return !isObject(item);
}
/**
 * Determines if an object exists in a particular array of objects.
 * It does support complex objects. ex -> user.contact.name
 *
 * @param objectToFind The object to find
 * @param objectList The array list of objects
 * @param attributeToMatch The object attribute to match on.
 * @return True if the object exists in the list.
 */
function doesObjectExistInList(objectToFind, objectList, attributeToMatch) {
    if (isNull(attributeToMatch)) {
        console.error("Attribute to match on is null!");
        return false;
    }
    if (isNull(objectToFind)) {
        console.error("The object to find is null!");
        return false;
    }
    if (isNotObject(objectToFind)) {
        console.error("The object to find is not an object! C'mon on, man!");
        return false;
    }
    if (isEmptyArray(objectList)) {
        return false;
    }
    const index = objectList.findIndex(item => {
        if (isNotObject(item)) {
            console.error("This item from the list is not an object!", item);
        }
        return getObjectValue(item, attributeToMatch) === getObjectValue(objectToFind, attributeToMatch);
    });
    return index > -1;
}
/**
 * This method is used to inject value to a string
 * @param string: The string we need to be replaced
 * @param replaceValue: the string to be replaced in the string
 * @param value: the value to replace the model
 */
function injectValueIntoString(string, replaceValue, value) {
    return string.replace(replaceValue, value);
}
/**
 * This is a common method used to compare values of two object
 * @param val1 value to compare
 * @param val2 value to compare
 */
function equals(val1, val2) {
    return val1 === val2;
}
/**
 * This is a method to check if two objects are equal
 * @param object1 object to compare
 * @param object2 object to compare
 */
function isEqualObjects(object1, object2) {
    return object1 && object2 && equals(object1.id, object2.id);
}
/**
 * Injects the API gateway value into an endpoint. Returns the new value.
 * By default, the replaced token is {environment.apiGateway}. Can override
 * by passing in the third parameter.
 *
 * @param endpoint the endpoint url
 * @param gateway the gateway url
 * @param replaceToken override the replace token
 */
function injectApiGateway(endpoint, gateway, replaceToken) {
    if (isNull(endpoint)) {
        console.error("The endpoint provided is null.");
        return;
    }
    if (isNull(gateway)) {
        console.error("The gateway provided is null.");
        return;
    }
    let replaceValue = '{environment.apiGateway}';
    if (isNotNull(replaceToken)) {
        replaceValue = replaceToken;
    }
    return injectValueIntoString(endpoint, replaceValue, gateway);
}

class JwtUtil {
    static urlBase64Decode(str) {
        let output = str.replace(/-/g, '+').replace(/_/g, '/');
        switch (output.length % 4) {
            case 0: {
                break;
            }
            case 2: {
                output += '==';
                break;
            }
            case 3: {
                output += '=';
                break;
            }
            default: {
                throw new Error('Illegal base64url string!');
            }
        }
        return decodeURIComponent(window.escape(window.atob(output)));
    }
    static decodeToken(token) {
        const parts = token.split('.');
        if (parts.length !== 3) {
            throw new Error('JWT must have 3 parts');
        }
        const decoded = this.urlBase64Decode(parts[1]);
        if (!decoded) {
            throw new Error('Cannot decode the token');
        }
        return JSON.parse(decoded);
    }
}

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
const DISABLED_FIELD_NAME = 'disabled';
const FALSE_FILTER_VALUE = 'false';
/**
 * This method returns a search filter group with a doesn not contain operator
 * for the items given. Note using filterList uses IN clause in
 *
 * @param fieldName The field name of search filter
 * @param items The array list of items
 * @param objectFieldNameOverride - OPTIONAL The property of object value if it is different from the fieldName.
 */
function createFilterGroupToRemoveExistingItems(fieldName, items, objectFieldNameOverride) {
    if (isNull(fieldName)) {
        console.error("Field name is null!!");
        return;
    }
    if (isEmptyArray(items)) {
        return undefined;
    }
    const filterGroup = new SearchFilterGroupData();
    filterGroup.andOrOperator = AndOrOperator.and;
    const filterField = new SearchFilterData();
    filterField.fieldName = fieldName;
    filterField.filterOperator = FilterOperator.neq;
    let objectFieldName = fieldName;
    if (isNotNull(objectFieldNameOverride)) {
        objectFieldName = objectFieldNameOverride;
    }
    items.forEach((item) => {
        filterField.filterValueList.push(getObjectValue(item, objectFieldName));
    });
    filterGroup.fieldFilters.push(filterField);
    return filterGroup;
}
/**
 * This is a function to assist in creating a new filter group
 *
 * @param fieldName Field Name to be populated
 * @param filterValue Field Value to be populated
 * @param filterOperator OPTIONAL filter Operator if not Equals
 */
function getFilterGroup({ fieldName, filterValue, filterOperator }) {
    const searchFilterGroup = new SearchFilterGroupData();
    searchFilterGroup.andOrOperator = AndOrOperator.and;
    const filterField = new SearchFilterData();
    filterField.fieldName = fieldName;
    if (isNotNull(filterOperator)) {
        filterField.filterOperator = filterOperator;
    }
    else {
        filterField.filterOperator = FilterOperator.eq;
    }
    filterField.filterValue = filterValue;
    searchFilterGroup.fieldFilters.push(filterField);
    return searchFilterGroup;
}
/**
 * This returns a search filter group with the field filter of disabled = false
 *
 * @param overrideFieldName OPTIONAL if the field name needs to be overwritten
 * @param overrideFilterValue OPTIONAL if the filter value needs to be overwritten
 */
function getFilterGroupForActiveRecords({ overrideFieldName, overrideFilterValue }) {
    let fieldName = DISABLED_FIELD_NAME;
    let filterValue = FALSE_FILTER_VALUE;
    if (isNotNull(overrideFieldName)) {
        fieldName = overrideFieldName;
    }
    if (isNotNull(overrideFilterValue)) {
        fieldName = overrideFilterValue;
    }
    const searchFilterGroup = new SearchFilterGroupData();
    searchFilterGroup.andOrOperator = AndOrOperator.and;
    const filterField = new SearchFilterData();
    filterField.fieldName = fieldName;
    filterField.filterOperator = FilterOperator.eq;
    filterField.filterValue = filterValue;
    searchFilterGroup.fieldFilters.push(filterField);
    return searchFilterGroup;
}
/**
 * Creates a paging model with 60000 records per page
 *
 * @Returns Paging Model
 */
function getMaxRecordsPagingModel() {
    return getPagingModel(60000);
}
/**
 * Creates a Paging model for the page size provided
 *
 * @param pageSize How many records per page
 */
function getPagingModel(pageSize) {
    const pagingModel = new PagingModel();
    pagingModel.pageSize = pageSize;
    return pagingModel;
}
/**
 * Creates a SearchFilterField with given fieldName, filterValue and filterOperator
 * @param fieldName - field to be filtered
 * @param filterValue - value to be filtered by
 * @param filterOperator - filter operator
 */
function getFieldFilter(fieldName, filterValue, filterOperator) {
    const searchFilterField = new SearchFilterData();
    searchFilterField.fieldName = fieldName;
    searchFilterField.filterValue = filterValue;
    searchFilterField.filterOperator = filterOperator;
    return searchFilterField;
}

class FabGridConversionUtility {
    constructor() {
    }
    static convertFabGridToSearchModel(fabGridState) {
        const model = new BaseModel();
        model.searchFilters = this.getFabGridSearchFilters(fabGridState.filter);
        model.paging = this.getFabGridPagingModel(fabGridState);
        model.sortList = this.getFabGridSortModels(fabGridState);
        return model;
    }
    static checkForDate(value, operator) {
        //Check if the value is a date
        if (value instanceof Date) {
            //Set the time to 0,0,0,0 for all operators since we don't have any time filters
            value.setUTCHours(0, 0, 0, 0);
            if (operator === FilterOperator.lte || operator === FilterOperator.gt) {
                //Set the time to the last minute of the day to include all records of that day
                value.setUTCHours(23, 59, 59, 999);
            }
            return value;
        }
        else {
            //Otherwise just return the value
            return value;
        }
    }
    static getFabGridSortModels(fabGridState) {
        let sortModel = [];
        if (isNotNull(fabGridState.sort) && Array.isArray(fabGridState.sort)) {
            for (const x in fabGridState.sort) {
                const sort = new SortModel();
                const sortFromGrid = fabGridState.sort[x];
                if (isNotNull(sortFromGrid.field) && isNotNull(sortFromGrid.dir)) {
                    sort.property = sortFromGrid.field;
                    sort.direction = sortFromGrid.dir.toUpperCase();
                    sortModel = [...sortModel, sort];
                }
            }
        }
        return sortModel;
    }
    static getFabGridPagingModel(fabGridState) {
        const paging = new PagingModel();
        if (fabGridState.skip === 0) {
            paging.page = 1;
        }
        else {
            //if skip is 20 and take is 20, then we're on page 2.
            paging.page = fabGridState.skip / fabGridState.take + 1;
        }
        //Maps Page Size if we are doing the dropdown.
        if (isNotNull(fabGridState.take)) {
            paging.pageSize = fabGridState.take;
        }
        return paging;
    }
    static getFabGridSearchFilters(fabGridFilter) {
        let searchFilterGroupData = new SearchFilterGroupData();
        if (isNotNull(fabGridFilter) && Array.isArray(fabGridFilter)) {
            for (const filter of fabGridFilter) {
                let searchFilterData = new SearchFilterData();
                searchFilterData.fieldName = filter.field;
                searchFilterData.filterOperator = FilterOperator[filter.operator.toString()];
                searchFilterData.filterValue = this.checkForDate(filter.value, searchFilterData.filterOperator);
                searchFilterGroupData = { ...searchFilterGroupData, fieldFilters: [...searchFilterGroupData.fieldFilters, searchFilterData] };
            }
        }
        return searchFilterGroupData;
    }
}

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DISABLED_FIELD_NAME, FALSE_FILTER_VALUE, FabGridConversionUtility, JwtUtil, addDays, contains, convertDate, convertFilterDate, convertToTime, createFilterGroupToRemoveExistingItems, doesObjectExistInList, doesOptionContainText, elementContainsElements, equals, filterOptionList, forceCopy, forceCopyArray, getFieldFilter, getFilterGroup, getFilterGroupForActiveRecords, getMaxRecordsPagingModel, getNestedPropertyArray, getNestedPropertyPath, getNestedValue, getObjectValue, getPagingModel, getTodayDate, getTopLevelPropertyPath, injectApiGateway, injectValueIntoString, isDate, isEmpty, isEmptyArray, isEmptyObject, isEqualObjects, isFilterDate, isNestedProperty, isNotEmpty, isNotEmptyArray, isNotEmptyObject, isNotNestedProperty, isNotNull, isNotNullNested, isNotObject, isNull, isNullNested, isObject, leftPad, removeSpaceCharactersFromString, removeSpacesFromString, subtractDays };
